/**
 * API Cache and Request Deduplication Utility for Pharmacy Web
 * 
 * Prevents duplicate API calls and caches responses to optimize performance.
 * Features:
 * - Request deduplication (multiple simultaneous calls result in single request)
 * - Time-based cache with configurable TTL
 * - Memory-efficient cache size limits
 * - Automatic cache invalidation
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

interface PendingRequest<T> {
  promise: Promise<T>;
  timestamp: number;
}

class ApiCache {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private pendingRequests: Map<string, PendingRequest<any>> = new Map();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes default
  private readonly MAX_CACHE_SIZE = 100;
  private readonly PENDING_REQUEST_TIMEOUT = 30000; // 30 seconds

  /**
   * Get data from cache if valid, otherwise execute the fetcher function
   * Deduplicates simultaneous requests for the same key
   */
  async fetch<T>(
    key: string,
    fetcher: () => Promise<T>,
    ttl: number = this.DEFAULT_TTL
  ): Promise<T> {
    // Check if data is in cache and still valid
    const cached = this.getFromCache<T>(key);
    if (cached !== null) {
      return cached;
    }

    // Check if there's already a pending request for this key
    const pending = this.pendingRequests.get(key);
    if (pending) {
      // Check if pending request is not stale
      if (Date.now() - pending.timestamp < this.PENDING_REQUEST_TIMEOUT) {
        return pending.promise;
      } else {
        // Remove stale pending request
        this.pendingRequests.delete(key);
      }
    }

    // Create new request
    const promise = fetcher()
      .then((data) => {
        // Store in cache
        this.setInCache(key, data, ttl);
        // Remove from pending
        this.pendingRequests.delete(key);
        return data;
      })
      .catch((error) => {
        // Remove from pending on error
        this.pendingRequests.delete(key);
        throw error;
      });

    // Store as pending request
    this.pendingRequests.set(key, {
      promise,
      timestamp: Date.now(),
    });

    return promise;
  }

  /**
   * Get data from cache if valid
   */
  private getFromCache<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) {
      return null;
    }

    // Check if cache entry is still valid
    const age = Date.now() - entry.timestamp;
    if (age > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  /**
   * Store data in cache
   */
  private setInCache<T>(key: string, data: T, ttl: number): void {
    // Enforce cache size limit
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      // Remove oldest entry
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  /**
   * Invalidate specific cache entry
   */
  invalidate(key: string): void {
    this.cache.delete(key);
    this.pendingRequests.delete(key);
  }

  /**
   * Invalidate cache entries matching a pattern
   */
  invalidatePattern(pattern: string | RegExp): void {
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
    let count = 0;

    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key);
        this.pendingRequests.delete(key);
        count++;
      }
    }

  }

  /**
   * Clear all cache
   */
  clear(): void {
    const cacheSize = this.cache.size;
    const pendingSize = this.pendingRequests.size;
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      entries: Array.from(this.cache.keys()),
    };
  }
}

// Export singleton instance
export const apiCache = new ApiCache();

// Helper functions for common cache keys
export const cacheKeys = {
  dashboard: (pharmacyId?: string) => 
    pharmacyId ? `dashboard:pharmacy:${pharmacyId}` : 'dashboard',
  formsList: (page: number, pharmacyId?: string) => 
    pharmacyId ? `forms:list:${page}:pharmacy:${pharmacyId}` : `forms:list:${page}`,
  travelerForm: (consultationId: string, travelerId: string) => 
    `traveler:form:${consultationId}:${travelerId}`,
  destinations: () => 'destinations:list',
  destinationByCode: (code: string) => `destinations:code:${code}`,
  destinationInfo: (country: string) => `destinations:info:${country}`,
};

// Cache TTL configurations (in milliseconds)
export const cacheTTL = {
  short: 1 * 60 * 1000,      // 1 minute - for frequently changing data (forms, dashboard)
  medium: 5 * 60 * 1000,     // 5 minutes - default for most data
  long: 15 * 60 * 1000,      // 15 minutes - for rarely changing data
  veryLong: 60 * 60 * 1000,  // 1 hour - for static data like destinations list
};
